import {
  DocumentQuery,
  FileUploadInfo,
  IDocumentService,
  IFileAccessService,
  ObjectTyped,
} from '@price-for-profit/micro-services';
import { unparse } from 'papaparse';
import { CLIENT_ID, CONTRACT_DOCUMENT_TYPE } from 'shared/constants/app';
import { INotificationService } from 'shared/services';
import { ContractDocument, ContractNotificationTemplate, I2PEnvironment } from 'shared/types';
import { browserDownloadCsv, formatDate, getContractEmailTemplate, getWeekExportStartEnd } from 'shared/utility';

export interface GetContractsForCustomerAndBrandProps {
  customer: string;
  brand: string;
}

export interface GetContractsForCustomerAndBrandsProps {
  customer: string;
  brands: Array<string>;
}

export interface DownloadFileProps {
  fileId: string;
  originalFileName: string;
}

export interface SendContractNotificationProps {
  contract: ContractDocument;
  template: ContractNotificationTemplate;
  environment: I2PEnvironment;
  comments: string;
}

export interface IContractService {
  createNewContract(contract: ContractDocument): Promise<ContractDocument>;
  updateContract(contract: ContractDocument): Promise<{ operationId: string }>;
  getContract(id?: string): Promise<ContractDocument | undefined>;
  getContractsForCustomerAndBrand(props: GetContractsForCustomerAndBrandProps): Promise<ContractDocument[]>;
  getContractsForCustomerAndBrands(props: GetContractsForCustomerAndBrandsProps): Promise<ContractDocument[]>;
  uploadContractFile(files: FormData): Promise<FileUploadInfo[]>;
  downloadContractFile(props: DownloadFileProps): Promise<void>;
  downloadContractInventory(props: GetContractsForCustomerAndBrandProps): Promise<boolean>;

  sendContractNotification(props: SendContractNotificationProps): Promise<void>;
}

export class ContractService implements IContractService {
  private shared: { type: string; clientId: string } = { type: CONTRACT_DOCUMENT_TYPE, clientId: CLIENT_ID };
  constructor(
    private documentService: IDocumentService,
    private fileAccessService: IFileAccessService,
    private notificationService: INotificationService
  ) {}
  async sendContractNotification({
    contract,
    template,
    environment,
    comments,
  }: SendContractNotificationProps): Promise<void> {
    await this.notificationService.sendEmail(getContractEmailTemplate({ contract, template, environment, comments }));
  }

  async uploadContractFile(files: FormData): Promise<FileUploadInfo[]> {
    return await this.fileAccessService.uploadFile({
      clientId: CLIENT_ID,
      files,
    });
  }

  async downloadContractFile(props: DownloadFileProps): Promise<void> {
    const file = await this.fileAccessService.getFile({
      clientId: CLIENT_ID,
      fileName: props.fileId,
    });

    this.handleBlob(file, props.originalFileName);
  }

  private handleBlob(blob: Blob, fileName: string) {
    const blobUrl = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    link.parentNode?.removeChild(link);
  }

  async createNewContract(contract: ContractDocument): Promise<ContractDocument> {
    const { data } = await this.documentService.create<ContractDocument, ContractDocument>({
      ...this.shared,
      payload: contract,
    });

    return data;
  }

  async updateContract(contract: ContractDocument) {
    const { metadata } = await this.documentService.update<ContractDocument>({
      ...this.shared,
      id: contract.id,
      payload: contract,
    });

    return metadata;
  }

  async getContract(id?: string): Promise<ContractDocument | undefined> {
    if (!id) {
      return undefined;
    }
    const { data } = await this.documentService.getById<ContractDocument>({
      clientId: CLIENT_ID,
      type: CONTRACT_DOCUMENT_TYPE,
      id,
    });
    return data;
  }

  private async getContractsDataForCustomerAndBrand({
    customer,
    brand,
  }: GetContractsForCustomerAndBrandProps): Promise<ContractDocument[]> {
    const brandSpecificQuery: DocumentQuery<ContractDocument> = {
      document: {
        brand: [
          {
            operator: 'contains',
            value: brand,
          },
        ],
        customerName: [
          {
            operator: 'eq',
            value: customer,
          },
        ],
      },
    };

    const multiBrandQuery: DocumentQuery<ContractDocument> = {
      document: {
        brand: [
          {
            operator: 'eq',
            value: 'Multiple',
          },
        ],
        customerName: [
          {
            operator: 'eq',
            value: customer,
          },
        ],
      },
    };

    const [multiResult, singleResult] = await Promise.all([
      this.documentService.query<ContractDocument>({
        clientId: CLIENT_ID,
        type: CONTRACT_DOCUMENT_TYPE,
        payload: multiBrandQuery,
      }),
      this.documentService.query<ContractDocument>({
        clientId: CLIENT_ID,
        type: CONTRACT_DOCUMENT_TYPE,
        payload: brandSpecificQuery,
      }),
    ]);

    return multiResult.data.concat(singleResult.data);
  }
  private async getContractsDataForCustomerAndBrands({
    customer,
    brands,
  }: GetContractsForCustomerAndBrandsProps): Promise<ContractDocument[]> {
    const queries = brands.map(brand => {
      const brandSpecificQuery: DocumentQuery<ContractDocument> = {
        document: {
          brand: [
            {
              operator: 'contains',
              value: brand,
            },
          ],
          customerName: [
            {
              operator: 'eq',
              value: customer,
            },
          ],
        },
      };

      return this.documentService.query<ContractDocument>({
        clientId: CLIENT_ID,
        type: CONTRACT_DOCUMENT_TYPE,
        payload: brandSpecificQuery,
      });
    });
    const results = await Promise.all(queries);

    return results.flatMap(result => result.data);
  }

  async getContractsForCustomerAndBrand({
    customer,
    brand,
  }: GetContractsForCustomerAndBrandProps): Promise<ContractDocument[]> {
    return await this.getContractsDataForCustomerAndBrand({ customer, brand });
  }

  async getContractsForCustomerAndBrands({
    customer,
    brands,
  }: GetContractsForCustomerAndBrandsProps): Promise<ContractDocument[]> {
    return await this.getContractsDataForCustomerAndBrands({ customer, brands });
  }

  async downloadContractInventory({ customer, brand }: GetContractsForCustomerAndBrandProps) {
    const today = new Date();
    const data = await this.getContractsDataForCustomerAndBrand({ customer, brand });
    const [start, end] = getWeekExportStartEnd(today);
    const fileName = `contract-inventory-${formatDate(start)}-${formatDate(end)}.csv`;

    if (!data || (data && !data.length)) {
      return false;
    }

    const fields = [
      'Document Name',
      'Brand',
      'Bill To',
      'Document Status',
      'Agreement Start Date',
      'Agreement End Date',
    ];

    const dataToExport = data.map(dataPeice => {
      return {
        fileName: dataPeice.originalDocumentName,
        brand: dataPeice.brand,
        billToCode: dataPeice.billToCode,
        documentStatus: dataPeice.status,
        agreementStartDate: dataPeice.startDate,
        agreementEndDate: dataPeice.endDate,
      };
    });

    const csvData = dataToExport.map(record => ObjectTyped.values(record));

    const csv = unparse({ data: csvData, fields });

    browserDownloadCsv(csv, fileName);

    return true;
  }
}
